/**
 * Координатор нескольких `<video>` с одним источником.
 *
 * В читалке разворот рисуется дважды и обрезается до левой и правой половины,
 * поэтому один ролик декодируют два независимых элемента. На слабых Android
 * один из них может буферизоваться или перестать выдавать кадры раньше другого.
 * Координатор не показывает частично работающий разворот: обе половины либо
 * играют вместе, либо одновременно стоят под общей иллюстрацией-постером.
 */
/** Небольшой дрейф исправляем скоростью, не перемоткой — она и создавала циклы. */
const RATE_CORRECTION_FROM_SEC = 0.06;
/** Большой дрейф исправляется только скрытой остановкой и общим seek. */
const HARD_RESYNC_FROM_SEC = 0.35;
const SLOW_RATE = 0.96;
const FAST_RATE = 1.04;
/** Если одна половина давно не выдавала кадр, а другая выдаёт — стоп для обеих. */
const FRAME_STALL_MS = 1_200;
const RECENT_FRAME_MS = 600;
const MONITOR_INTERVAL_MS = 250;
/** HAVE_FUTURE_DATA недостаточно: на холодном MP4 это всего несколько кадров. */
const MIN_BUFFER_AHEAD_SEC = 1.5;
/** Короткие ролики книги надёжнее один раз загрузить целиком под постером. */
const FULL_BUFFER_MAX_DURATION_SEC = 15;
/** currentTime у конца loop не всегда успевает сам перескочить в ноль. */
const LOOP_END_MARGIN_SEC = 0.08;
const STALE_GROUP_MS = 60_000;
const groups = new Map();
function notify(group, playing) {
    group.running = playing;
    for (const member of group.members)
        member.onPlaybackChange?.(playing);
}
function seek(element, time, force = false) {
    if (!force && Math.abs(element.currentTime - time) <= RATE_CORRECTION_FROM_SEC)
        return;
    try {
        element.currentTime = time;
    }
    catch {
        // До metadata браузер запомнит стартовую позицию не всегда — повторим позже.
    }
}
/**
 * Первый запуск и возврат после `waiting` разрешаем только с реальным запасом.
 * `readyState=4` — обещание браузера доиграть без остановки; если браузер его
 * не выставляет, принимаем явно загруженные полторы секунды (либо весь остаток
 * короткого ролика). Обычный `canplay` (`readyState=3`) раньше запускал видео
 * с буфером на 250 мс и создавал цикл start → waiting → seek.
 */
function hasPlaybackBuffer(element, time) {
    if (element.readyState < HTMLMediaElement.HAVE_FUTURE_DATA)
        return false;
    const duration = element.duration;
    // Для обычного MP4 metadata обязана дать конечную длительность раньше,
    // чем ролик можно считать готовым. readyState=4 при duration=NaN оказался
    // ложным сигналом Chrome и запускал холодный ролик кусками по 200–400 мс.
    if (!Number.isFinite(duration) || duration <= 0)
        return false;
    if (Number.isFinite(duration) && duration > 0 && duration <= FULL_BUFFER_MAX_DURATION_SEC) {
        for (let index = 0; index < element.buffered.length; index += 1) {
            const start = element.buffered.start(index);
            const end = element.buffered.end(index);
            if (start <= time + RATE_CORRECTION_FROM_SEC && end >= duration - LOOP_END_MARGIN_SEC) {
                return true;
            }
        }
        return false;
    }
    if (element.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA)
        return true;
    const required = Number.isFinite(duration)
        ? Math.min(MIN_BUFFER_AHEAD_SEC, Math.max(0, duration - time))
        : MIN_BUFFER_AHEAD_SEC;
    for (let index = 0; index < element.buffered.length; index += 1) {
        const start = element.buffered.start(index);
        const end = element.buffered.end(index);
        if (start <= time + RATE_CORRECTION_FROM_SEC && end - time >= required)
            return true;
    }
    return false;
}
function refreshReadiness(group) {
    for (const member of group.members) {
        member.ready = hasPlaybackBuffer(member.element, group.time);
    }
}
/** Самая поздняя позиция без ошибочного возврата назад на границе loop. */
function latestGroupTime(group) {
    const times = [...group.members]
        .map(({ element }) => element.currentTime)
        .filter(Number.isFinite);
    if (times.length === 0)
        return group.time;
    const minimum = Math.min(...times);
    const maximum = Math.max(...times);
    const duration = group.leader?.element.duration;
    if (duration && Number.isFinite(duration)) {
        // Обе половинки могут одновременно остановиться прямо на duration, не
        // успев обработать `loop`. Возобновлять с конца бессмысленно — play()
        // немедленно снова отдаст waiting.
        if (minimum >= duration - LOOP_END_MARGIN_SEC)
            return 0;
        if (maximum - minimum > duration / 2)
            return minimum;
    }
    return maximum;
}
function clearRetry(group) {
    if (group.retryId === null)
        return;
    globalThis.clearTimeout(group.retryId);
    group.retryId = null;
}
function stopTogether(group, blocked) {
    group.time = latestGroupTime(group);
    group.revision += 1;
    group.starting = false;
    clearRetry(group);
    if (blocked)
        blocked.ready = false;
    notify(group, false);
    for (const member of group.members) {
        member.playing = false;
        member.element.playbackRate = 1;
        if (!member.element.paused) {
            member.expectedPause = true;
            member.element.pause();
        }
    }
}
function driftFromLeader(member, leader) {
    let drift = member.element.currentTime - leader.element.currentTime;
    const duration = leader.element.duration;
    if (duration && Number.isFinite(duration) && Math.abs(drift) > duration / 2) {
        drift -= Math.sign(drift) * duration;
    }
    return drift;
}
function isAligned(group) {
    const leader = group.leader;
    if (!leader)
        return false;
    return [...group.members].every((member) => Math.abs(driftFromLeader(member, leader)) < HARD_RESYNC_FROM_SEC);
}
function finishStart(group, revision) {
    if (group.revision !== revision || !group.starting)
        return;
    if (![...group.members].every((member) => member.playing))
        return;
    if (!isAligned(group)) {
        recoverTogether(group);
        return;
    }
    group.starting = false;
    notify(group, true);
}
function startTogether(group) {
    if (group.running ||
        group.starting ||
        group.failed ||
        group.members.size === 0 ||
        [...group.members].some((member) => !member.autoPlay || !member.ready)) {
        return;
    }
    group.starting = true;
    group.buffering = false;
    const revision = ++group.revision;
    const target = group.time;
    for (const member of group.members) {
        member.expectedPause = false;
        member.playing = false;
        member.element.playbackRate = 1;
        seek(member.element, target);
    }
    void Promise.all([...group.members].map(({ element }) => element.play())).then(() => finishStart(group, revision), () => {
        if (group.revision === revision) {
            stopTogether(group);
        }
    });
}
function retryWhenReady(group) {
    clearRetry(group);
    group.retryId = globalThis.setTimeout(() => {
        group.retryId = null;
        refreshReadiness(group);
        if ([...group.members].every((member) => member.ready)) {
            startTogether(group);
        }
        else if (group.members.size > 0 && [...group.members].every((member) => member.autoPlay)) {
            retryWhenReady(group);
        }
    }, MONITOR_INTERVAL_MS);
}
function recoverTogether(group, blocked) {
    group.buffering = false;
    stopTogether(group, blocked);
    for (const member of group.members)
        seek(member.element, group.time, true);
    retryWhenReady(group);
}
/**
 * `waiting` на холодном старте — не рассинхрон и не повод перематывать декодеры.
 * Ставим обе половины на паузу в их текущих позициях и ждём запас буфера.
 * Второе событие от соседней половины схлопывается в уже начатое ожидание.
 */
function waitForBuffer(group, blocked) {
    blocked.ready = false;
    if (!group.running && !group.starting)
        return;
    group.buffering = true;
    stopTogether(group, blocked);
    refreshReadiness(group);
    retryWhenReady(group);
}
function correctRunningDrift(group) {
    const leader = group.leader;
    if (!leader || !group.running)
        return;
    group.time = leader.element.currentTime;
    for (const member of group.members) {
        if (member === leader) {
            member.element.playbackRate = 1;
            continue;
        }
        const drift = driftFromLeader(member, leader);
        if (Math.abs(drift) >= HARD_RESYNC_FROM_SEC) {
            recoverTogether(group, member);
            return;
        }
        const nextRate = drift < -RATE_CORRECTION_FROM_SEC
            ? FAST_RATE
            : drift > RATE_CORRECTION_FROM_SEC
                ? SLOW_RATE
                : 1;
        if (member.element.playbackRate !== nextRate) {
            member.element.playbackRate = nextRate;
        }
    }
}
function monitorFrames(group) {
    if (!group.running || group.members.size < 2)
        return;
    const timestamps = [...group.members]
        .map((member) => member.lastFrameAt)
        .filter((value) => value !== null);
    if (timestamps.length !== group.members.size)
        return;
    const now = Date.now();
    const hasMovingHalf = timestamps.some((timestamp) => now - timestamp <= RECENT_FRAME_MS);
    const stalled = [...group.members].find((member) => member.lastFrameAt !== null && now - member.lastFrameAt > FRAME_STALL_MS);
    if (hasMovingHalf && stalled) {
        recoverTogether(group, stalled);
    }
}
function startMonitor(group) {
    if (group.monitorId !== null)
        return;
    group.monitorId = globalThis.setInterval(() => monitorFrames(group), MONITOR_INTERVAL_MS);
}
function stopMonitor(group) {
    if (group.monitorId === null)
        return;
    globalThis.clearInterval(group.monitorId);
    group.monitorId = null;
}
function watchFrames(member) {
    if (typeof member.element.requestVideoFrameCallback !== 'function')
        return;
    const watch = () => {
        if (!member.active)
            return;
        member.frameRequestId = member.element.requestVideoFrameCallback(() => {
            if (!member.active)
                return;
            member.lastFrameAt = Date.now();
            watch();
        });
    };
    watch();
}
function prune(now) {
    for (const [key, group] of groups) {
        if (group.members.size === 0 &&
            group.emptyAt !== null &&
            now - group.emptyAt > STALE_GROUP_MS) {
            groups.delete(key);
        }
    }
}
/** Подключить видео к общей группе воспроизведения и вернуть функцию отписки. */
export function joinVideoGroup(key, element, options = {}) {
    const now = Date.now();
    prune(now);
    let group = groups.get(key);
    if (!group) {
        group = {
            time: 0,
            emptyAt: null,
            leader: null,
            running: false,
            starting: false,
            buffering: false,
            failed: false,
            revision: 0,
            monitorId: null,
            retryId: null,
            members: new Set(),
        };
        groups.set(key, group);
    }
    const joined = group;
    const member = {
        element,
        autoPlay: options.autoPlay ?? true,
        onPlaybackChange: options.onPlaybackChange,
        ready: hasPlaybackBuffer(element, group.time),
        playing: false,
        expectedPause: false,
        lastFrameAt: null,
        frameRequestId: null,
        active: true,
    };
    // Новый клон половины не должен присоединяться посреди видимого проигрывания.
    if (joined.members.size > 0)
        stopTogether(joined);
    joined.members.add(member);
    joined.leader ??= member;
    joined.emptyAt = null;
    options.onPlaybackChange?.(false);
    startMonitor(joined);
    watchFrames(member);
    const markReady = () => {
        member.ready = hasPlaybackBuffer(element, joined.time);
        seek(element, joined.time);
        startTogether(joined);
    };
    const onLoadedMetadata = () => {
        seek(element, joined.time);
        markReady();
    };
    const onPlaying = () => {
        member.ready = true;
        member.playing = true;
        member.lastFrameAt = Date.now();
        finishStart(joined, joined.revision);
    };
    const onPause = () => {
        member.playing = false;
        if (member.expectedPause) {
            member.expectedPause = false;
            return;
        }
        if (joined.running || joined.starting) {
            stopTogether(joined, member);
        }
    };
    const onWaiting = () => waitForBuffer(joined, member);
    const onStalled = () => waitForBuffer(joined, member);
    const onError = () => {
        joined.failed = true;
        stopTogether(joined, member);
    };
    const onTimeUpdate = () => {
        if (joined.leader !== member)
            return;
        if (joined.buffering)
            return;
        joined.time = element.currentTime;
        if (joined.running)
            correctRunningDrift(joined);
        else
            for (const other of joined.members)
                if (other !== member)
                    seek(other.element, joined.time);
    };
    element.addEventListener('loadedmetadata', onLoadedMetadata);
    const onLoadedData = () => markReady();
    const onCanPlay = () => markReady();
    const onProgress = () => markReady();
    element.addEventListener('loadeddata', onLoadedData);
    element.addEventListener('canplay', onCanPlay);
    element.addEventListener('progress', onProgress);
    element.addEventListener('playing', onPlaying);
    element.addEventListener('pause', onPause);
    element.addEventListener('waiting', onWaiting);
    element.addEventListener('stalled', onStalled);
    element.addEventListener('error', onError);
    element.addEventListener('timeupdate', onTimeUpdate);
    queueMicrotask(markReady);
    return () => {
        element.removeEventListener('loadedmetadata', onLoadedMetadata);
        element.removeEventListener('loadeddata', onLoadedData);
        element.removeEventListener('canplay', onCanPlay);
        element.removeEventListener('progress', onProgress);
        element.removeEventListener('playing', onPlaying);
        element.removeEventListener('pause', onPause);
        element.removeEventListener('waiting', onWaiting);
        element.removeEventListener('stalled', onStalled);
        element.removeEventListener('error', onError);
        element.removeEventListener('timeupdate', onTimeUpdate);
        member.active = false;
        if (member.frameRequestId !== null && typeof element.cancelVideoFrameCallback === 'function') {
            element.cancelVideoFrameCallback(member.frameRequestId);
        }
        joined.members.delete(member);
        if (joined.leader === member)
            joined.leader = joined.members.values().next().value ?? null;
        if (joined.members.size === 0) {
            joined.running = false;
            joined.starting = false;
            joined.buffering = false;
            joined.failed = false;
            joined.revision += 1;
            joined.emptyAt = Date.now();
            stopMonitor(joined);
            clearRetry(joined);
        }
        else {
            recoverTogether(joined);
        }
    };
}
//# sourceMappingURL=video-sync.js.map